import { useEffect, useState } from "react";
import { FormHelperText, Grid, Stack, Typography } from "@mui/material";
import { Input } from "@/components/elements/Input";
import { Checkbox } from "@/components/elements/Checkbox";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ClearIcon from "@mui/icons-material/Clear";
import { black, blue, text } from "@/theme/colors";
import { FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { TJobForm, TOnboardingSupport } from "@/features/job/types/JobForm";
import { DEFAULT_ONBOARDING_SUPPORTS } from "@/types/enum";
import { MAX_CHARACTER_INPUT_VALIDATION } from "@/constants/common";

type TOnboardingSupportProps = {
  isDraft?: boolean;
  onboardingSupportRef: React.MutableRefObject<(HTMLDivElement | null)>;
};

export const OnboardingSupportFields: FC<TOnboardingSupportProps> = ({ onboardingSupportRef }) => {
  const { control, setValue } = useFormContext<TJobForm>();
  const [fields, setFields] = useState<TOnboardingSupport[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const selected = useWatch({ control, name: "onboardingSupport" }) || [];
  const defaultLabels = DEFAULT_ONBOARDING_SUPPORTS.map((s) => s.label);

  useEffect(() => {
    const hasSelected = Array.isArray(selected) && selected.length > 0;
    if (fields.length === 0 && hasSelected) {
      setFields(selected);
      setErrors(selected.map(() => ""));
    }
  }, [selected]);

  useEffect(() => {
    if (fields.length > 0) {
      const filteredFields = fields.filter((f) => !(f.label.trim() === "" && !f.checked));
      setValue("onboardingSupport", filteredFields);
    }
  }, [fields, setValue]);

  const handleCheckboxChange = (index: number, checked: boolean) => {
    setFields(prev => {
    const updated = prev.map((f, i) =>
      i === index ? { ...f, checked } : f
    );

    setErrors(prevErr => {
      const newErrors = [...prevErr];
      const label = updated[index].label;

      if (checked && !label) {
        newErrors[index] = "「受け入れ体制・サポート」を入力してください。";
      } else if (label.length > MAX_CHARACTER_INPUT_VALIDATION) {
        newErrors[index] = `「受け入れ体制・サポート」は最大${MAX_CHARACTER_INPUT_VALIDATION}文字で入力してください。`;
      } else {
        newErrors[index] = "";
      }
      return newErrors;
    });

    return updated;
  });
  };

  const handleFieldChange = (index: number, newLabel: string) => {
    const validValue = newLabel.replace(/\s+/g, " ").trimStart();

    setFields(prev => {
      const updated = prev.map((f, i) =>
        i === index ? { ...f, label: validValue } : f
      );

      setErrors(prevErr => {
        const newErrors = [...prevErr];
        const { checked, label } = updated[index];

        if (!label && checked) {
          newErrors[index] = "「受け入れ体制・サポート」を入力してください。";
        } else if (label.length > MAX_CHARACTER_INPUT_VALIDATION) {
          newErrors[index] = `「受け入れ体制・サポート」は最大${MAX_CHARACTER_INPUT_VALIDATION}文字で入力してください。`;
        } else {
          newErrors[index] = "";
        }

        return newErrors;
      });

      return updated;
    });
  };

  const handleAddField = () => {
    if (fields.length < 10) {
      setFields([...fields, { checked: false, label: "" }]);
    }
  };

  const handleRemoveField = () => {
    if (fields.length > DEFAULT_ONBOARDING_SUPPORTS.length) {
      setFields((prev) => prev.slice(0, -1));
    }
  };

  return (
    <Stack ref={onboardingSupportRef}>
      <Typography variant="cap12">
        受け入れ体制・サポート
      </Typography>

      {fields.map((field, index) => {
        const isDefault = defaultLabels.includes(field.label);
        const isFirstAppearance = fields.findIndex(f => f.label === field.label) === index;

        return (
          <Grid container spacing={2} alignItems="flex-start" key={index} sx={{ mb: 1 }}>
            <Grid item xs={1}>
              <Checkbox
                checked={field.checked}
                onChange={(e) => handleCheckboxChange(index, e.target.checked)}
              />
            </Grid>
            <Grid item xs={11}>
              {isDefault && isFirstAppearance ? (
                <Typography
                  component={'span'}
                  variant="body2"
                  lineHeight={1.5}
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleCheckboxChange(index, !field.checked)}
                >
                  {field.label}
                </Typography>
              ) : (
                <>
                  <Input
                    placeholder="入力してください"
                    value={field.label}
                    onChange={(e) => handleFieldChange(index, e.target.value)}
                    error={ errors[index] ? true : false }
                    sx={{
                      width: '100%'
                    }}
                  />
                  {errors[index] && <FormHelperText sx={{ textAlign: "left", color: text.error }}>{errors[index]}</FormHelperText>}
                </>
              )}
            </Grid>
          </Grid>
        );
      })}

      {(fields.length < 10 || fields.length > 1) && (
        <Stack direction="row" alignItems="center" spacing={2} mt={1}>
          {fields.length < 10 && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ cursor: "pointer" }}
              onClick={handleAddField}
            >
              <AddCircleOutlineIcon
                sx={{
                  color: blue[80],
                  fontSize: 18,
                }}
              />
              <Typography variant="cap12">受け入れ体制・サポートを追加</Typography>
            </Stack>
          )}

          {fields.length > DEFAULT_ONBOARDING_SUPPORTS.length && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ cursor: "pointer" }}
              onClick={handleRemoveField}
            >
              <ClearIcon sx={{ color: black[80], fontSize: 18 }} />
              <Typography variant="cap12Light">削除</Typography>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};
